import { LIMIT } from '@/constants';
import { ForumNotificationType, ForumPostHotDateType, ForumPostStatus, Sort } from '@/types';
import {
  deleteForumPostDocument,
  fetchForumNotificationsDocument,
  fetchForumPostDocument,
  fetchForumReviewsDocument,
  fetchForumTagDocument,
  fetchForumTagsDocument,
  fetchHotForumPostsByTagDocument,
  fetchHotForumPostsDocument,
  fetchMyForumPostCountDocument,
  fetchMyForumPostsDocument,
  fetchNewForumPostsByActivityDocument,
  fetchNewForumPostsByTagDocument,
  fetchNewForumPostsDocument,
  fetchUnAuditForumPostsDocument,
  forumDownPostDocument,
  forumPostShareDocument,
  forumReviewPostDocument,
  forumUpPostDocument,
  getForumCustomerDocument,
  getHotForumPostsByActivityDocument,
  hasUnreadNotificationsDocument,
  markNotificationsAsReadDocument,
  upsertForumPostDocument,
} from '../graphql/docs';
import { fetcher } from '../graphql/fetcher';

/**
 * 获取话题
 * @param options
 * @returns
 */
export const fetchForumTags = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchForumTagsDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: variables.options?.take || LIMIT,
        filter: {
          name: {
            contains: variables.options?.name,
          },
        },
      },
    },
  });
  return res.data.value?.forumTags;
};

/**
 * 获取话题详情
 * @param variables
 * @returns
 */
export const fetchForumTag = async (id: string) => {
  const res = await fetcher.query(fetchForumTagDocument, {
    variables: {
      forumTagId: id,
    },
  });
  return res.data.value?.forumTag;
};

/**
 * 获取热门帖子
 * @param variables
 * @returns
 */
export const fetchHotForumPosts = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchHotForumPostsDocument, {
    variables: {
      isWish: variables?.isWish || false,
      forumPostHotDateType: variables.forumPostHotDateType,
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
        filter: {
          title: {
            contains: variables.options?.title,
          },
        },
      },
    },
  });
  return res.data.value?.getHotForumPosts;
};

/**
 * 获取最新帖子
 * @param variables
 * @returns
 */
export const fetchNewForumPosts = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchNewForumPostsDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
    },
  });
  return res.data.value?.getNewForumPosts;
};

/**
 * 发帖
 * @param variables
 * @returns
 */
export const fetchUpsertForumPost = async (variables: AnyObject) => {
  const res = await fetcher.mutation(upsertForumPostDocument, {
    input: variables,
  });
  return res.data?.upsertForumPost;
};

/**
 * 获取帖子详情
 * @param variables
 * @returns
 */
export const fetchForumPostDetail = async (id: string) => {
  const res = await fetcher.query(fetchForumPostDocument, {
    variables: {
      id,
    },
  });
  return res.data.value?.getForumTopicByPid;
};

/**
 * 帖子取消点赞
 * @param variables
 * @returns
 */
export const fetchForumPostDown = async (pid: string) => {
  const res = await fetcher.mutation(forumDownPostDocument, {
    pid: pid.toString(),
  });
  return res.data?.forumDownPost;
};

/**
 * 帖子点赞
 * @param variables
 * @returns
 */
export const fetchForumPostUp = async (pid: string) => {
  const res = await fetcher.mutation(forumUpPostDocument, {
    pid: pid.toString(),
  });
  return res.data?.forumUpPost;
};

/**
 * 根据话题获取热门帖子
 * @param variables
 * @returns
 */
export const fetchHotForumPostsByTag = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchHotForumPostsByTagDocument, {
    variables: {
      forumTagId: variables.forumTagId,
      forumPostHotDateType: variables.forumPostHotDateType,
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
    },
  });
  return res.data.value?.getHotForumPostsByTag;
};

/**
 * 根据话题获取最新帖子
 * @param variables
 * @returns
 */
export const fetchNewForumPostsByTag = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchNewForumPostsByTagDocument, {
    variables: {
      forumTagId: variables.forumTagId,
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
    },
  });
  return res.data.value?.getNewForumPostsByTag;
};

/**
 * 获取待审核帖子
 * @param variables
 * @returns
 */
export const fetchUnAuditForumPosts = async (variables: AnyObject) => {
  const inStatus = [variables.options.status];
  if (variables.options.status === ForumPostStatus.Pending) {
    inStatus.push(ForumPostStatus.Refused);
  }
  const res = await fetcher.query(fetchUnAuditForumPostsDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
        filter: {
          status: {
            in: inStatus,
          },
        },
        sort: {
          updatedAt: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.getUnAuditForumPosts;
};

/**
 * 帖子评论
 * @param variables
 * @returns
 */
export const fetchForumReviewPost = async (variables: AnyObject) => {
  const res = await fetcher.mutation(forumReviewPostDocument, { ...variables });
  return res.data?.forumReviewPost;
};

/**
 * 获取帖子评论
 * @param variables
 * @returns
 */
export const fetchForumReviews = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchForumReviewsDocument, {
    variables: {
      pid: variables.pid.toString(),
      level: variables.level,
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
        sort: {
          // createdAt: variables.level === 0 ? Sort.DESC : Sort.ASC,
          ...variables.options.sort,
        },
      },
    },
  });
  return res.data.value?.getForumReviews;
};

/**
 * 获取帖子通知
 * @param variables
 * @returns
 */
export const fetchForumNotifications = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchForumNotificationsDocument, {
    variables: {
      notificationType: variables.notificationType,
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
    },
  });
  return res.data.value?.forumNotifications;
};

/**
 * 删除帖子
 * @param variables
 * @returns
 */
export const fetchDeleteForumPost = async (variables: AnyObject) => {
  const res = await fetcher.mutation(deleteForumPostDocument, {
    forumPostId: variables.forumPostId,
  });
  return res.data?.deleteForumPost;
};

/**
 * 获取我的全部帖子
 * @param variables
 * @returns
 */
export const fetchMyForumPosts = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchMyForumPostsDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
        sort: {
          updatedAt: Sort.DESC,
        },
      },
    },
  });
  return res.data.value?.getMyForumPosts;
};

/**
 * 获取我的全部点赞数和评论数
 * @param variables
 * @returns
 */
export const fetchMyForumPostCount = async () => {
  const res = await fetcher.query(fetchMyForumPostCountDocument, {
    variables: {},
  });
  return res.data.value?.getMyForumPostCount;
};

/**
 * 获取帖子作者信息
 * @param variables
 * @returns
 */
export const fetchForumCustomer = async () => {
  const res = await fetcher.query(getForumCustomerDocument, {
    variables: {},
  });
  return res.data.value?.getForumCustomer;
};

/**
 * 标记通知为已读
 * @param variables
 * @returns
 */
export const fetchMarkNotificationsAsRead = async (notificationType: ForumNotificationType) => {
  const res = await fetcher.mutation(markNotificationsAsReadDocument, {
    notificationType,
  });
  return res.data?.markNotificationsAsRead;
};

/**
 * 是否有未读通知
 * @param variables
 * @returns
 */
export const fetchHasUnreadNotifications = async (notificationType: ForumNotificationType) => {
  const res = await fetcher.query(hasUnreadNotificationsDocument, {
    variables: {
      notificationType,
    },
  });
  return res.data.value?.hasUnreadNotifications;
};

/**
 * 获取活动排行榜帖子 - 最热
 * @param variables
 * @returns
 */
export const fetchHotForumPostsByActivity = async (variables: AnyObject) => {
  const res = await fetcher.query(getHotForumPostsByActivityDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
      activityId: variables.activityId,
      forumPostHotDateType: variables?.forumPostHotDateType || ForumPostHotDateType.alltime,
    },
  });
  return res.data.value?.getHotForumPostsByActivity;
};

/**
 * 获取活动排行榜帖子 - 最新
 * @param variables
 * @returns
 */
export const fetchNewForumPostsByActivity = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchNewForumPostsByActivityDocument, {
    variables: {
      options: {
        skip: LIMIT * variables.options.skip,
        take: LIMIT,
      },
      activityId: variables.activityId,
      isWish: false,
    },
  });
  return res.data.value?.getNewForumPostsByActivity;
};

/**
 * 帖子分享上报
 * @param variables
 * @returns
 */
export const fetchForumPostShare = async (id: string) => {
  const res = await fetcher.mutation(forumPostShareDocument, {
    id,
  });
  return res.data?.forumPostShare;
};
